import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { categoryService, statisticsService } from '../../../apiServices';
import { useRefresh } from '../../../contexts/RefreshContext';
import { Category, CategoryTransactionsResponse } from '../../../apiServices/types';
import { useNotification } from '../../../contexts/NotificationContext';
import { Modal, ConfirmationModal } from '../../UI';
import { formatCurrency, formatDate } from '../../../utils';
import './CategoryManagement.css';

const CategoryManagement: React.FC = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [editName, setEditName] = useState('');
  const [showConfirmDelete, setShowConfirmDelete] = useState<Category | null>(null);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [showConfirmCleanup, setShowConfirmCleanup] = useState<Category | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCleaningUp, setIsCleaningUp] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [categoryReceipts, setCategoryReceipts] = useState<CategoryTransactionsResponse | null>(null);
  const [loadingReceipts, setLoadingReceipts] = useState(false);
  const [expandedReceipts, setExpandedReceipts] = useState<Set<string>>(new Set());
  const { showNotification } = useNotification();

  const loadCategories = useCallback(async (showLoader = false) => {
    try {
      if (showLoader) {
        setLoading(true);
      }
      const data = await categoryService.getAll();
      setCategories(data);
    } catch (error) {
      showNotification('Помилка при завантаженні категорій', 'error');
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  }, [showNotification]);

  useRefresh(() => loadCategories(true));

  useEffect(() => {
    let mounted = true;
    const loadData = async () => {
      try {
        setLoading(true);
        const data = await categoryService.getAll();
        if (mounted) {
          setCategories(data);
        }
      } catch (error) {
        if (mounted) {
          showNotification('Помилка при завантаженні категорій', 'error');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };
    loadData();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setEditName(category.name);
  };

  const handleCloseEdit = () => {
    if (editingCategory && editName.trim() !== editingCategory.name) {
      setShowConfirmClose(true);
    } else {
      setEditingCategory(null);
      setEditName('');
    }
  };

  const handleConfirmClose = () => {
    setEditingCategory(null);
    setEditName('');
    setShowConfirmClose(false);
  };

  const handleCancelClose = () => {
    setShowConfirmClose(false);
  };

  const handleSaveEdit = async () => {
    if (!editingCategory || !editName.trim()) return;

    try {
      await categoryService.update(editingCategory.id, { name: editName.trim() });
      showNotification('Категорію успішно оновлено', 'success');
      setEditingCategory(null);
      setEditName('');
      await loadCategories(false);
    } catch (error: any) {
      if (error.response?.data?.message) {
        showNotification(error.response.data.message, 'error');
      } else {
        showNotification('Помилка при оновленні категорії', 'error');
      }
    }
  };

  const handleDelete = (category: Category) => {
    setShowConfirmDelete(category);
  };

  const handleDeleteCancel = () => {
    setShowConfirmDelete(null);
  };

  const handleCleanup = (category: Category) => {
    setShowConfirmCleanup(category);
  };

  const handleDeleteConfirm = async () => {
    if (!showConfirmDelete || isDeleting) return;

    try {
      setIsDeleting(true);
      await categoryService.delete(showConfirmDelete.id);
      showNotification('Категорію видалено', 'success');
      setShowConfirmDelete(null);
      await loadCategories(false);
    } catch (error: any) {
      if (error.response?.data?.message) {
        showNotification(error.response.data.message, 'error');
      } else {
        showNotification('Помилка при видаленні категорії', 'error');
      }
      setShowConfirmDelete(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCleanupConfirm = async () => {
    if (!showConfirmCleanup || isCleaningUp) return;

    try {
      setIsCleaningUp(true);
      await categoryService.cleanupArchivedTransactions(showConfirmCleanup.id);
      showNotification('Архівні транзакції успішно видалено', 'success');
      setShowConfirmCleanup(null);
      await loadCategories(false);
    } catch (error: any) {
      if (error.response?.data?.message) {
        showNotification(error.response.data.message, 'error');
      } else {
        showNotification('Помилка при очищенні архіву', 'error');
      }
      setShowConfirmCleanup(null);
    } finally {
      setIsCleaningUp(false);
    }
  };

  const handleCleanupCancel = () => {
    setShowConfirmCleanup(null);
  };

  const canDeleteCategory = (category: Category) => {
    const activeCount = category.transactionsCount || 0;
    // Can delete if no active transactions
    return activeCount === 0;
  };

  const handleCategoryClick = (category: Category, e: React.MouseEvent) => {
    // Don't open modal if clicking on buttons
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    setSelectedCategory(category);
    loadCategoryReceipts(category.id);
  };

  const loadCategoryReceipts = useCallback(async (categoryId: string) => {
    setLoadingReceipts(true);
    try {
      const data = await statisticsService.getCategoryTransactions(categoryId, { period: 'custom' }, 'expenses');
      setCategoryReceipts(data);
      setExpandedReceipts(new Set());
    } catch (error) {
      showNotification('Помилка при завантаженні чеків', 'error');
    } finally {
      setLoadingReceipts(false);
    }
  }, [showNotification]);

  const handleCloseReceiptsModal = () => {
    setSelectedCategory(null);
    setCategoryReceipts(null);
    setExpandedReceipts(new Set());
  };

  const toggleReceipt = (receiptId: string) => {
    setExpandedReceipts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(receiptId)) {
        newSet.delete(receiptId);
      } else {
        newSet.add(receiptId);
      }
      return newSet;
    });
  };

  const handleEditReceipt = (receiptId: string) => {
    navigate(`/dashboard/expenses/${receiptId}`);
    handleCloseReceiptsModal();
  };

  const groupedReceipts = React.useMemo(() => {
    if (!categoryReceipts) return [];

    const grouped = categoryReceipts.transactions.reduce((acc, transaction) => {
      if (!acc[transaction.receiptId]) {
        acc[transaction.receiptId] = {
          receiptId: transaction.receiptId,
          receiptName: transaction.receiptName,
          accountName: transaction.accountName,
          date: transaction.date,
          transactions: [],
          totalAmount: 0
        };
      }
      acc[transaction.receiptId].transactions.push(transaction);
      acc[transaction.receiptId].totalAmount += transaction.amount;
      return acc;
    }, {} as Record<string, { receiptId: string; receiptName: string; accountName: string; date: string; transactions: typeof categoryReceipts.transactions; totalAmount: number }>);

    return Object.values(grouped)
      .map(receipt => ({
        ...receipt,
        transactions: receipt.transactions.sort((a, b) => b.amount - a.amount)
      }))
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [categoryReceipts]);

  if (loading) {
    return (
      <div className="category-management-page">
        <div className="card">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Завантаження категорій...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="category-management-page">
      <div className="category-management-header">
        <div className="category-management-header-content">
          <h2 className="category-management-header-title">Керування категоріями</h2>
          <p className="category-management-header-subtitle">Редагування та видалення категорій транзакцій</p>
          <p className="category-management-header-count">Кількість категорій: {categories.length}</p>
        </div>
      </div>

      <div className="categories-list">
        {categories.length === 0 ? (
          <div className="empty-state card">
            <div className="empty-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                <polyline points="9 22 9 12 15 12 15 22"></polyline>
              </svg>
            </div>
            <div className="empty-title">Немає категорій</div>
            <div className="empty-text">
              Категорії будуть автоматично створюватися при додаванні транзакцій
            </div>
          </div>
        ) : (
          categories.map((category) => {
            const activeCount = category.transactionsCount || 0;
            const archivedCount = category.archivedTransactionsCount || 0;

            return (
              <div 
                key={category.id} 
                className="category-management-card"
                onClick={(e) => handleCategoryClick(category, e)}
              >
                <div className="category-management-card-header">
                  <div className="category-management-card-title-section">
                    <div className="category-management-title-content">
                      <h3 className="category-management-name">{category.name}</h3>
                    </div>
                  </div>
                  <div className="category-management-card-actions">
                    <button
                      className="category-management-action-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(category);
                      }}
                      title="Редагувати"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </button>
                    {canDeleteCategory(category) && (
                      <button
                        className="category-management-action-icon category-management-action-icon-danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(category);
                        }}
                        title="Видалити"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3,6 5,6 21,6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      </button>
                    )}
                  </div>
                </div>

                <div className="category-management-card-body">
                  <div className="category-management-stats-grid">
                    <div className="category-management-stat">
                      <div className="category-management-stat-label">Позицій</div>
                      <div className="category-management-stat-value">{activeCount}</div>
                    </div>
                    {archivedCount > 0 && (
                      <div className="category-management-stat category-management-stat-archived">
                        <div className="category-management-stat-header">
                          <div className="category-management-stat-label">Архівних позицій</div>
                          <button
                            className="category-management-stat-cleanup"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCleanup(category);
                            }}
                            title="Очистити архів"
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6"></polyline>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                              <line x1="10" y1="11" x2="10" y2="17"></line>
                              <line x1="14" y1="11" x2="14" y2="17"></line>
                            </svg>
                          </button>
                        </div>
                        <div className="category-management-stat-value">{archivedCount}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Edit Modal */}
      {editingCategory && (
        <Modal
          title="Редагувати категорію"
          isOpen={true}
          onClose={handleCloseEdit}
          footer={
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCloseEdit}
              >
                Скасувати
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSaveEdit}
                disabled={!editName.trim() || editName === editingCategory.name}
              >
                Зберегти
              </button>
            </>
          }
        >
          <div className="form-group">
            <label htmlFor="category-name" className="form-label">
              Назва категорії
            </label>
            <input
              id="category-name"
              type="text"
              className="form-input"
              value={editName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditName(e.target.value)}
              placeholder="Введіть назву категорії"
            />
          </div>
        </Modal>
      )}

      {/* Confirmation Modal for unsaved changes */}
      <ConfirmationModal
        isOpen={showConfirmClose}
        title="Скасувати зміни?"
        message="У вас є незбережені зміни. Ви впевнені, що хочете закрити форму без збереження?"
        confirmText="Так"
        cancelText="Ні"
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
        variant="warning"
        isLoading={false}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!showConfirmDelete}
        title="Видалити категорію?"
        message={
          showConfirmDelete 
            ? `Ви впевнені, що хочете видалити категорію "${showConfirmDelete.name}"? Цю дію неможливо скасувати.${
                (showConfirmDelete.archivedTransactionsCount || 0) > 0 
                  ? `\n\nУвага: ця категорія містить ${showConfirmDelete.archivedTransactionsCount} архівних транзакцій. Вони будуть втрачені після видалення категорії.`
                  : ''
              }`
            : ''
        }
        confirmText="Видалити"
        cancelText="Скасувати"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        variant="danger"
        isLoading={isDeleting}
      />

      {/* Cleanup Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!showConfirmCleanup}
        title="Очистити архівні транзакції?"
        message={
          showConfirmCleanup 
            ? `Ви впевнені, що хочете назавжди видалити ${showConfirmCleanup.archivedTransactionsCount} архівних транзакцій з категорії "${showConfirmCleanup.name}"? Цю дію неможливо скасувати.`
            : ''
        }
        confirmText="Очистити"
        cancelText="Скасувати"
        onConfirm={handleCleanupConfirm}
        onCancel={handleCleanupCancel}
        variant="warning"
        isLoading={isCleaningUp}
      />

      {/* Category Receipts Modal */}
      {selectedCategory && (
        <Modal
          isOpen={true}
          onClose={handleCloseReceiptsModal}
          title={`Чеки: ${selectedCategory.name}`}
          size="md"
        >
          {loadingReceipts ? (
            <div className="category-management-page loading-spinner">
              <div className="category-management-page spinner"></div>
              <p>Завантаження чеків...</p>
            </div>
          ) : categoryReceipts && categoryReceipts.transactions.length > 0 ? (
            <div className="category-receipts-content">
              <div className="category-receipts-summary">
                <div className="category-receipts-summary-item">
                  <span className="category-receipts-summary-label">Всього чеків:</span>
                  <span className="category-receipts-summary-value">{groupedReceipts.length}</span>
                </div>
                <div className="category-receipts-summary-item">
                  <span className="category-receipts-summary-label">Загальна сума:</span>
                  <span className="category-receipts-summary-value expenses">
                    {formatCurrency(categoryReceipts.totalAmount)} ₴
                  </span>
                </div>
              </div>

              <div className="category-receipts-list">
                {groupedReceipts.map((receipt) => {
                  const isExpanded = expandedReceipts.has(receipt.receiptId);
                  return (
                    <div key={receipt.receiptId} className="category-receipt-group">
                      <div 
                        className="category-receipt-header"
                        onClick={() => toggleReceipt(receipt.receiptId)}
                      >
                        <div className="category-receipt-info">
                          <div className="category-receipt-name">{receipt.receiptName}</div>
                          <div className="category-receipt-meta">
                            <span className="category-receipt-date">{formatDate(receipt.date)}</span>
                            <span className="category-receipt-account">{receipt.accountName}</span>
                          </div>
                        </div>
                        <div className="category-receipt-actions">
                          <div className="category-receipt-total expenses">
                            {formatCurrency(receipt.totalAmount)} ₴
                          </div>
                          <div className={`category-receipt-arrow ${isExpanded ? 'expanded' : ''}`}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                          </div>
                        </div>
                      </div>
                      
                      {isExpanded && (
                        <div className="category-receipt-transactions">
                          {receipt.transactions.map((transaction) => (
                            <div key={transaction.positionId} className="category-receipt-transaction-item">
                              <div className="category-receipt-transaction-content">
                                <div className="category-receipt-transaction-name">{transaction.positionName}</div>
                                <div className="category-receipt-transaction-amount expenses">
                                  {formatCurrency(transaction.amount)} ₴
                                </div>
                              </div>
                            </div>
                          ))}
                          <div className="category-receipt-edit-action">
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => handleEditReceipt(receipt.receiptId)}
                            >
                              Редагувати чек
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
              </div>
              <div className="empty-title">Немає чеків</div>
              <div className="empty-text">Для цієї категорії немає чеків</div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
};

export default CategoryManagement;